import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, Plus, Check, X, TicketCheck, UserPlus, Copy, Share2, Search, User, RotateCcw, Clock } from 'lucide-react';
import type { GuestPass, GuestPassStatus } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Select, Modal, Skeleton, EmptyState, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useAsync } from '../lib/useAsync';
import { formatDMY } from '../lib/format';
import { copyText, shareText } from '../lib/share';
import { isIntlPhone, normalizeIntlPhone, INTL_PHONE_PLACEHOLDER } from '../lib/phone';
import {
  getGuestPasses,
  createGuestPass,
  lookupGuestPassByCode,
  redeemGuestPassByCode,
  listMembers,
} from '../lib/gymApi';

/**
 * Staff view of guest passes. We deliberately DON'T list invited guests by name — a walk-in could
 * read a name off the screen and claim to be them. Instead the desk enters the code the guest brings,
 * looks up who they are and who invited them, verifies the person, then confirms the check-in.
 */
export function GuestsPage() {
  const { data, loading, reload } = useAsync(getGuestPasses);
  const [inviting, setInviting] = useState(false);
  const nav = useNavigate();

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={data ? `${data.summary.total} passes` : undefined}
        title="Guests"
        description="Members invite a friend for a free visit. Check a guest in by their code — no list to browse, so nobody can claim a name that isn't theirs."
        actions={
          data?.enabled && (
            <Button onClick={() => setInviting(true)}>
              <Plus className="size-4" aria-hidden />
              <span className="hidden sm:inline">Invite a guest</span>
            </Button>
          )
        }
      />

      {loading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-40 rounded-card" />
          <Skeleton className="h-20 rounded-card" />
        </div>
      ) : !data?.enabled ? (
        <EmptyState
          icon={<Ticket className="size-6" aria-hidden />}
          title="Guest passes are off"
          description="Turn on guest passes in Settings to let members invite a friend for a free visit."
          action={<Button onClick={() => nav('/settings')}>Go to settings</Button>}
        />
      ) : (
        <>
          <CheckInCard onCheckedIn={reload} />

          {/* Aggregate report only — no names of guests who haven't arrived yet. */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Total" value={data.summary.total} />
            <Stat label="Active" value={data.summary.active} />
            <Stat label="Redeemed" value={data.summary.redeemed} />
            <Stat label="Joined" value={`${data.summary.converted} · ${data.summary.conversionRate}%`} />
          </div>

          <CheckedIn passes={data.passes} />
        </>
      )}

      {inviting && <InviteModal onClose={() => setInviting(false)} onSaved={() => { setInviting(false); reload(); }} />}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold text-ink">{value}</p>
    </Card>
  );
}

function StatusPill({ status }: { status: GuestPassStatus }) {
  const map: Record<GuestPassStatus, { label: string; cls: string }> = {
    ISSUED: { label: 'Active', cls: 'bg-accent text-accent-ink' },
    REDEEMED: { label: 'Checked in', cls: 'bg-ok-soft text-ok-ink' },
    CONVERTED: { label: 'Joined', cls: 'bg-ok-ink text-white' },
    EXPIRED: { label: 'Expired', cls: 'bg-surface text-muted' },
    CANCELLED: { label: 'Cancelled', cls: 'bg-surface text-muted' },
  };
  const s = map[status];
  return <span className={cn('rounded-pill px-2 py-0.5 text-xs font-semibold', s.cls)}>{s.label}</span>;
}

/** History of guests who've already checked in — safe to show (they've been verified), grouped by day. */
function CheckedIn({ passes }: { passes: GuestPass[] }) {
  const groups = useMemo(() => {
    const rows = passes.filter((p) => p.redeemedAt);
    rows.sort((a, b) => new Date(b.redeemedAt!).getTime() - new Date(a.redeemedAt!).getTime());
    const out: { day: string; rows: GuestPass[] }[] = [];
    for (const p of rows) {
      const day = dayLabel(p.redeemedAt!);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(p);
      else out.push({ day, rows: [p] });
    }
    return out;
  }, [passes]);

  if (groups.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="font-display text-lg font-semibold text-ink">Checked in</h2>
      <p className="text-sm text-muted">Guests who've already been checked in.</p>
      <div className="mt-3 flex flex-col gap-4">
        {groups.map((g) => (
          <div key={g.day}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">{g.day}</p>
            <div className="flex flex-col gap-2">
              {g.rows.map((p) => (
                <Card key={p.id} className="flex items-center gap-3 p-3.5">
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-ok-soft text-ok-ink">
                    <Check className="size-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-ink">{p.guestName}</p>
                      {p.status === 'CONVERTED' && <StatusPill status="CONVERTED" />}
                    </div>
                    <p className="truncate text-xs text-muted">Invited by {p.inviterName ?? 'a member'}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-xs text-muted tabular">
                    <Clock className="size-3.5" aria-hidden /> {timeLabel(p.redeemedAt!)}
                  </span>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** The desk check-in flow: enter code → look up → verify who they are → confirm check-in. */
function CheckInCard({ onCheckedIn }: { onCheckedIn: () => void }) {
  const nav = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pass, setPass] = useState<GuestPass | null>(null); // looked-up (not yet checked in)
  const [checkedIn, setCheckedIn] = useState(false);

  function reset() {
    setCode('');
    setPass(null);
    setError(null);
    setCheckedIn(false);
  }

  async function lookUp() {
    setError(null);
    setPass(null);
    setCheckedIn(false);
    if (!code.trim()) {
      setError('Enter the code the guest gives you.');
      return;
    }
    setBusy(true);
    try {
      setPass(await lookupGuestPassByCode(code.trim()));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'No guest pass with that code.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmCheckIn() {
    if (!pass) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await redeemGuestPassByCode(pass.code);
      setPass(updated);
      setCheckedIn(true);
      onCheckedIn(); // refresh the summary + checked-in history
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not check them in.');
    } finally {
      setBusy(false);
    }
  }

  function addAsMember() {
    if (!pass) return;
    const params = new URLSearchParams({ name: pass.guestName, guestPass: pass.id });
    if (pass.guestPhone) params.set('phone', pass.guestPhone);
    if (pass.guestEmail) params.set('email', pass.guestEmail);
    nav(`/members/new?${params.toString()}`);
  }

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div>
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ink">
          <TicketCheck className="size-5 text-accent-strong" aria-hidden /> Check in a guest
        </h2>
        <p className="mt-0.5 text-sm text-muted">Enter the code the guest brings. You'll see who they are and who invited them — confirm it's really them, then check them in.</p>
      </div>

      {checkedIn && pass ? (
        <div className="flex flex-col items-center gap-2 rounded-card bg-ok-soft/60 px-4 py-6 text-center">
          <span className="grid size-12 place-items-center rounded-full bg-ok-soft text-ok-ink">
            <Check className="size-6" aria-hidden />
          </span>
          <p className="font-medium text-ink">{pass.guestName} is checked in</p>
          <p className="text-sm text-muted">Welcome them in.</p>
          <div className="mt-2 flex flex-wrap justify-center gap-2">
            {pass.status !== 'CONVERTED' && (
              <Button size="sm" onClick={addAsMember}>
                <UserPlus className="size-4" aria-hidden /> Add as member
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={reset}>
              <RotateCcw className="size-4" aria-hidden /> Check in another
            </Button>
          </div>
        </div>
      ) : pass ? (
        <GuestVerify
          pass={pass}
          busy={busy}
          error={error}
          onConfirm={confirmCheckIn}
          onBack={reset}
        />
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Input
              label="Guest code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && lookUp()}
              autoFocus
              placeholder="e.g. K7P2QXM"
            />
          </div>
          <Button className="sm:mb-0.5" onClick={lookUp} loading={busy}>
            {!busy && <Search className="size-4" aria-hidden />} Look up
          </Button>
        </div>
      )}

      {!pass && error && (
        <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
          {error}
        </p>
      )}
    </Card>
  );
}

/** The verification panel: shows who the pass belongs to + who invited them, then Confirm/Back. */
function GuestVerify({
  pass,
  busy,
  error,
  onConfirm,
  onBack,
}: {
  pass: GuestPass;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const canCheckIn = pass.status === 'ISSUED';
  const contact = pass.guestPhone || pass.guestEmail;
  const blockedMsg: Record<string, string> = {
    REDEEMED: 'This pass has already been checked in.',
    CONVERTED: 'This guest has already joined as a member.',
    EXPIRED: 'This pass has expired.',
    CANCELLED: 'This pass was cancelled.',
  };

  return (
    <div className="flex flex-col gap-4 rounded-card border border-line bg-surface/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-accent-soft text-ink">
            <User className="size-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-lg font-semibold text-ink">{pass.guestName}</p>
              <StatusPill status={pass.status} />
            </div>
            <p className="mt-0.5 text-sm text-muted">Invited by {pass.inviterName ?? 'a member'}</p>
            {contact && <p className="text-sm text-muted">{contact}</p>}
            <p className="mt-0.5 text-xs text-muted">
              Code <code className="rounded bg-elevated px-1 font-mono text-ink-soft">{pass.code}</code>
              {' · '}
              {pass.status === 'CONVERTED' && pass.convertedMemberName
                ? `joined as ${pass.convertedMemberName}`
                : `valid until ${formatDMY(pass.expiresAt)}`}
            </p>
          </div>
        </div>
      </div>

      {canCheckIn ? (
        <p className="rounded-control bg-elevated px-3.5 py-2 text-sm text-ink-soft">
          Ask the guest for their name and check it matches, then confirm.
        </p>
      ) : (
        <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
          {blockedMsg[pass.status] ?? "This pass can't be checked in."}
        </p>
      )}

      {error && (
        <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onBack} disabled={busy}>Back</Button>
        {canCheckIn && (
          <Button onClick={onConfirm} loading={busy}>
            {!busy && <Check className="size-4" aria-hidden />} Confirm check-in
          </Button>
        )}
      </div>
    </div>
  );
}

function InviteModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { data: membersPage } = useAsync(() => listMembers({ size: 1000 }));
  const members = membersPage?.items ?? [];

  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [memberId, setMemberId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<GuestPass | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function copyCode() {
    if (!created) return;
    setNote((await copyText(created.code)) ? 'Code copied' : 'Could not copy');
  }

  async function share() {
    if (!created) return;
    const text = `You've got a free guest pass! Show this code at the front desk: ${created.code} (valid until ${formatDMY(created.expiresAt)}).`;
    const r = await shareText(text, 'Guest pass');
    if (r === 'copied') setNote('Invite copied');
    else if (r === 'failed') setNote('Could not share');
  }

  async function save() {
    setError(null);
    if (!guestName.trim()) {
      setError("Enter the guest's name.");
      return;
    }
    if (guestPhone.trim() && !isIntlPhone(guestPhone)) {
      setError('Enter the phone in international format, e.g. +263 77 123 4567.');
      return;
    }
    setSaving(true);
    try {
      const pass = await createGuestPass({
        guestName: guestName.trim(),
        guestEmail: guestEmail.trim() || null,
        guestPhone: guestPhone.trim() ? normalizeIntlPhone(guestPhone) : null,
        memberId: memberId || null,
      });
      setCreated(pass);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not create the pass.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={created ? onSaved : onClose}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Invite a guest</h2>
          <button type="button" aria-label="Close" onClick={created ? onSaved : onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>

        {created ? (
          <div className="flex flex-col items-center gap-2 py-2 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-ok-soft text-ok-ink">
              <Check className="size-6" aria-hidden />
            </span>
            <p className="text-sm text-muted">Guest pass for {created.guestName}</p>
            <p className="font-display text-3xl font-bold tracking-[0.2em] text-ink">{created.code}</p>
            <p className="text-sm text-muted">Valid until {formatDMY(created.expiresAt)}. They show this code at the desk.</p>
            <div className="mt-2 flex w-full gap-2">
              <Button variant="outline" className="flex-1" onClick={copyCode}>
                <Copy className="size-4" aria-hidden /> Copy code
              </Button>
              <Button className="flex-1" onClick={share}>
                <Share2 className="size-4" aria-hidden /> Share
              </Button>
            </div>
            {note && <p className="text-xs font-medium text-ok-ink">{note}</p>}
            <button type="button" onClick={onSaved} className="mt-1 text-sm font-medium text-muted hover:text-ink">Done</button>
          </div>
        ) : (
          <>
            <Input label="Guest name" value={guestName} onChange={(e) => setGuestName(e.target.value)} autoFocus />
            <Input label="Guest email (optional)" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} hint="If set, we email them the pass." />
            <Input
              label="Guest phone (optional)"
              type="tel"
              inputMode="tel"
              value={guestPhone}
              onChange={(e) => setGuestPhone(e.target.value)}
              placeholder={INTL_PHONE_PLACEHOLDER}
              hint="International format so we can text them the pass."
            />
            <Select label="On behalf of" value={memberId} onChange={(e) => setMemberId(e.target.value)} hint="Counts against that member's monthly allowance. Leave as the business for an open invite.">
              <option value="">The business (no member)</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName}
                </option>
              ))}
            </Select>

            {error && (
              <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
                {error}
              </p>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={save} loading={saving}>{!saving && <Check className="size-4" aria-hidden />} Create pass</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
